'use client';

import React, { useState, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { GenericTag, type StatusType } from '@/styles/GenericTag';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, FileText, X, Trash2, Download, Copy, UploadCloud } from 'lucide-react';
import { GenericTable, GenericTableColumn } from '@/styles/GenericTable';
import { GenericSelect } from '@/styles/GenericForm';
import { NewItemButton, SearchButton, SecondaryButton, ThreeDotMenuButton } from '@/styles/StandardButtons';
import { bidApiService } from '../services/bid-api-service';
import { useEntityList } from '@/hooks/useEntityList';
import { useCompanyLookups } from '@/hooks/useCompanyLookups';
import { exportEntityCsv } from '@/lib/csv';
import { fetchEntityAttachments } from '@/lib/entity-attachments';
import { BidForm } from './bid-form';
import { bidPackageApi } from '../bid-packages/api';
import { useBidPdfUpload, type PdfUpload } from '../hooks/use-bid-pdf-upload';
import { useUser } from '@/contexts/UserContext';
import { useProject, getDefaultProjectIdForNewRecord } from '@/components/ProjectSelector';
import type { GenericAttachment } from '@/styles/GenericAttachmentsSection';
import type { BidItem } from '../types';

interface BidRecord {
  id: string;
  bid_id?: string | null;
  project_id?: string;
  project_name?: string;
  vendor_id?: string | null;
  project?: {
    id: string;
    name: string;
  };
  name?: string | null;
  subcontractor_name: string;
  scope?: string;
  total_amount?: number;
  amount?: number;
  date_received: string;
  valid_until?: string;
  submitted_at?: string | null;
  accepted_at?: string | null;
  rejected_at?: string | null;
  payment_terms?: string;
  status: string;
  category?: string;
  other_category?: string | null;
  notes?: string | null;
  bid_number?: string | null;
  description?: string;
}

interface BidManagementProps {
  onCompareBids?: (bidIds: string[]) => void;
}

const UPLOAD_TAGS: Record<PdfUpload['status'], { tag: StatusType; label: string }> = {
  uploading: { tag: 'pending', label: 'Uploading' },
  analyzing: { tag: 'pending', label: 'Analyzing' },
  needs_project: { tag: 'under_review', label: 'Select project' },
  saving: { tag: 'in_progress', label: 'Creating bid' },
  completed: { tag: 'active', label: 'Created' },
  error: { tag: 'rejected', label: 'Failed' },
};

export function BidManagement({ onCompareBids }: BidManagementProps = {}) {
  const { companyId, user, userRole } = useUser();
  // an assigned vendor sees ONLY its own bids (RLS + project membership decide which): the table and the search, nothing to create, copy, delete or compare
  const vendorView = userRole === 'vendor';
  const { selectedProject } = useProject();
  // The list: ONE server-driven query (search + sort + page) -- the database searches, sorts and pages every bid the
  // user may see (also by its project); the counters come with each row (hooks/useEntityList.ts).
  const list = useEntityList<any, BidRecord>({
    entityType: 'bid',
    enabled: !!companyId,
    mode: 'pages',
    includeCounts: true,
    defaultSort: { columnKey: 'bid_id', direction: 'asc', sortKey: 'bid_id' },
    mapRow: (row) => row,
  });
  const bids = list.items;
  const loadBids = list.reload;
  const { projects } = useCompanyLookups(!!companyId, { includeInactiveProjects: true });
  const [showBidForm, setShowBidForm] = useState(false);
  const [selectedBid, setSelectedBid] = useState<BidRecord | null>(null);
  const [selectedBidItems, setSelectedBidItems] = useState<BidItem[]>([]);
  const [bidAttachments, setBidAttachments] = useState<GenericAttachment[]>([]);
  const [vendorBidPackage, setVendorBidPackage] = useState<React.ComponentProps<typeof BidForm>['bidPackage']>(undefined);
  const [selectedBids, setSelectedBids] = useState<string[]>([]);
  const [isBulkActionRunning, setIsBulkActionRunning] = useState(false);
  // comments / files changed inside the form without saving the bid: the list's counters need a refresh on close
  const relatedChangedRef = useRef(false);

  const { uploads, isProcessing, handleDrop, chooseProject, dismiss } = useBidPdfUpload({
    contextProjectId: getDefaultProjectIdForNewRecord(selectedProject),
    session: { companyId, userId: user?.id },
    onBidCreated: loadBids,
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true,
    noClick: true, // Disable click-to-upload, only allow drag & drop
    disabled: vendorView || isProcessing || showBidForm, // Disable dropzone when form is open (and for a vendor: no upload, no AI)
  });

  const closeForm = () => {
    setShowBidForm(false);
    setSelectedBid(null);
    setSelectedBidItems([]);
    setBidAttachments([]);
    setVendorBidPackage(undefined);
    if (relatedChangedRef.current) {
      relatedChangedRef.current = false;
      loadBids();
    }
  };

  const handleRowClick = async (bid: BidRecord) => {
    // the bid with its items and its files, in parallel; its comments load inside the form without blocking it
    const [detailedBid, attachments] = await Promise.all([
      bidApiService.getBid(bid.id).catch((error) => {
        console.error('Failed to load bid details:', error);
        return null;
      }),
      fetchEntityAttachments('bid', bid.id, { ascending: false }),
    ]);
    const opened = detailedBid || bid;
    // a vendor edits a bid of a package through the package-mode form; once its assignment is revoked the package is not readable any more (the title falls back)
    if (vendorView && opened.bid_package_id) {
      const pkg = await bidPackageApi.get(opened.bid_package_id).catch(() => null);
      setVendorBidPackage({ id: opened.bid_package_id, title: pkg?.title || 'Bid package', project_id: opened.project_id, vendor_id: opened.vendor_id, vendor_name: opened.subcontractor_name || '', status: pkg?.status });
    }
    setSelectedBid(opened);
    setSelectedBidItems(detailedBid?.bid_items || []);
    setBidAttachments(attachments);
    setShowBidForm(true);
  };

  const handleCopySelected = async () => {
    if (!selectedBids.length) {
      alert('Please select at least one bid to copy.');
      return;
    }

    setIsBulkActionRunning(true);
    let successCount = 0;
    let failureCount = 0;

    try {
      for (const bidId of selectedBids) {
        try {
          const { bid_items = [], id: _ignore, created_at, updated_at, bid_id, ...rest } = await bidApiService.getBid(bidId);
          const createdBid = await bidApiService.createBid({
            project_id: rest.project_id || null,
            vendor_id: rest.vendor_id || null,
            name: rest.name || null,
            description: rest.description || null,
            amount: rest.amount ?? rest.total_amount ?? 0,
            status: rest.status || 'pending',
            date_received: rest.date_received,
            submitted_at: rest.submitted_at,
            accepted_at: rest.accepted_at,
            rejected_at: rest.rejected_at,
            subcontractor_name: rest.subcontractor_name,
            bid_number: rest.bid_number,
            valid_until: rest.valid_until,
            payment_terms: rest.payment_terms,
            notes: rest.notes,
            category: rest.category,
            other_category: rest.other_category,
          });
          await bidApiService.addBidItems(createdBid.id, bid_items);
          successCount++;
        } catch (copyError) {
          console.error('Error copying bid:', copyError);
          failureCount++;
        }
      }

      if (successCount) {
        loadBids();
        setSelectedBids([]);
      }

      if (failureCount > 0) {
        alert(`Copied ${successCount} bid(s). ${failureCount} failed.`);
      } else if (successCount > 0) {
        alert(`Copied ${successCount} bid(s) successfully.`);
      }
    } finally {
      setIsBulkActionRunning(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedBids.length) {
      alert('Please select at least one bid to delete.');
      return;
    }

    if (!window.confirm(`Delete ${selectedBids.length} bid(s)? This action cannot be undone.`)) {
      return;
    }

    setIsBulkActionRunning(true);
    let successCount = 0;
    let failureCount = 0;

    try {
      for (const bidId of selectedBids) {
        try {
          await bidApiService.deleteBid(bidId);
          successCount++;
        } catch (deleteError) {
          console.error('Error deleting bid:', deleteError);
          failureCount++;
        }
      }

      if (successCount) {
        loadBids();
        setSelectedBids([]);
      }

      if (failureCount > 0) {
        alert(`Deleted ${successCount} bid(s). ${failureCount} failed.`);
      } else if (successCount > 0) {
        alert(`Deleted ${successCount} bid(s) successfully.`);
      }
    } finally {
      setIsBulkActionRunning(false);
    }
  };

  // Export to CSV: the bids selected in the table, or -- with nothing selected -- EVERY bid of the current search,
  // read by the backend (not only the page that is showing).
  const handleExportSelected = () => exportEntityCsv(list.exportRows, selectedBids, 'bids', 'bids');

  // Map bid statuses to GenericTag status types
  const mapBidStatusToTag = (status: string): 'received' | 'under_review' | 'awarded' | 'rejected' => {
    switch (status?.toLowerCase()) {
      case 'received':
        return 'received'; // Cyan
      case 'under_review':
        return 'under_review'; // Yellow
      case 'awarded':
        return 'awarded'; // Green
      case 'rejected':
        return 'rejected'; // Red
      default:
        return 'received';
    }
  };

  const formatCurrency = (amount: number | null | undefined) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    // Parse ISO date string (YYYY-MM-DD) to avoid timezone issues
    const parts = dateString.split('T')[0].split('-');
    if (parts.length === 3) {
      const year = parts[0];
      const month = parts[1];
      const day = parts[2];
      return `${day}/${month}/${year}`;
    }
    // Fallback for other formats
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Column definitions for GenericTable
  const columns: GenericTableColumn<BidRecord>[] = [
    {
      key: 'bid_id',
      header: 'Bid ID',
      width: '6.5rem',
      sortable: true,
      sortKey: 'bid_id',
      render: (row) => <span className="font-medium">{row.bid_id || '—'}</span>,
    },
    {
      key: 'subcontractor_name',
      header: 'Vendor',
      width: '8rem',
      sortable: true,
      sortKey: 'subcontractor_name',
      render: (row) => row.subcontractor_name || '—',
    },
    {
      key: 'project_name',
      header: 'Project',
      width: '10rem',
      sortable: true,
      sortKey: 'project',
      render: (row) => (row as any).project?.name || row.project_name || 'No project assigned',
    },
    {
      key: 'amount',
      header: 'Amount',
      width: '6.5rem',
      sortable: true,
      sortKey: 'amount',
      render: (row) => formatCurrency(row.amount || row.total_amount),
    },
    {
      key: 'status',
      header: 'Status',
      width: '6.5rem',
      sortable: true,
      sortKey: 'status',
      render: (row) => (
        <GenericTag 
          type="status" 
          value={mapBidStatusToTag(row.status)}
        />
      ),
    },
    {
      key: 'date_received',
      header: 'Date Received',
      width: '6.125rem', // 8.125rem - 2rem
      sortable: true,
      sortKey: 'date_received',
      render: (row) => formatDate(row.date_received),
    },
    {
      key: 'valid_until',
      header: 'Valid Until',
      width: '6.125rem', // 8.125rem - 2rem
      sortable: true,
      sortKey: 'valid_until',
      render: (row) => row.valid_until ? formatDate(row.valid_until) : 'N/A',
    },
    {
      key: 'attachment_count',
      header: 'Attachments',
      width: '6.25rem',
      sortable: true,
      sortKey: 'attachment_count',
      render: (row) => <span className="text-center block">{(row as any).attachment_count || 0}</span>,
    },
    {
      key: 'comment_count',
      header: 'Comments',
      width: '6.25rem',
      sortable: true,
      sortKey: 'comment_count',
      render: (row) => <span className="text-center block">{(row as any).comment_count || 0}</span>,
    },
  ];

  // Menu items for ThreeDotMenuButton
  const menuItems = [
    {
      label: 'Copy',
      icon: <Copy className="w-4 h-4" />,
      onClick: handleCopySelected,
      disabled: isBulkActionRunning,
    },
    {
      label: 'Delete',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: handleDeleteSelected,
      destructive: true,
      disabled: isBulkActionRunning,
    },
    {
      label: 'Import',
      icon: <UploadCloud className="w-4 h-4" />,
      onClick: () => {},
    },
    {
      label: 'Export',
      icon: <Download className="w-4 h-4" />,
      onClick: handleExportSelected,
    },
  ];

  return (
    <>
      {showBidForm && (
        <BidForm
          bid={selectedBid}
          bidItems={selectedBidItems}
          attachments={bidAttachments}
          projects={projects}
          bidPackage={vendorBidPackage}
          onRelatedChange={() => { relatedChangedRef.current = true; }}
          onClose={closeForm}
          onSubmit={() => {
            relatedChangedRef.current = false;
            loadBids();
          }}
          isOpen={showBidForm}
        />
      )}
    <div className="space-y-6">
      {/* PDF uploads in progress */}
      {uploads.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Uploading Bids</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {uploads.map((upload) => {
                const { tag, label } = UPLOAD_TAGS[upload.status];
                const busy = upload.status === 'uploading' || upload.status === 'analyzing' || upload.status === 'saving';
                return (
                  <div key={upload.id} className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText className="h-4 w-4 shrink-0" />
                        <span className="font-medium truncate">{upload.file.name}</span>
                        <GenericTag type="status" value={tag}>{label}</GenericTag>
                      </div>
                      {!busy && (
                        <Button variant="ghost" size="sm" aria-label="Dismiss" onClick={() => dismiss(upload.id)} className="h-6 w-6 p-0">
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                    </div>

                    {busy && <Progress value={upload.progress} className="h-2" />}

                    {upload.analysis && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="font-medium">Vendor:</span>
                          <p className="text-gray-600">{upload.analysis.subcontractor_name}</p>
                        </div>
                        <div>
                          <span className="font-medium">Category:</span>
                          <p className="text-gray-600">{upload.analysis.category}</p>
                        </div>
                        <div>
                          <span className="font-medium">Date:</span>
                          <p className="text-gray-600">{upload.analysis.date}</p>
                        </div>
                        <div>
                          <span className="font-medium">Amount:</span>
                          <p className="text-gray-600">{upload.analysis.total_amount ? formatCurrency(upload.analysis.total_amount) : 'N/A'}</p>
                        </div>
                      </div>
                    )}

                    {upload.status === 'needs_project' && (
                      <div className="flex items-end gap-3">
                        <div className="w-72">
                          <p className="text-sm text-gray-600 mb-1">Which project is this bid for?</p>
                          <GenericSelect
                            value=""
                            onValueChange={(projectId) => chooseProject(upload.id, projectId)}
                            required
                            placeholder="Select"
                            contentClassName="max-h-[18.75rem] overflow-y-auto"
                            options={projects.map((project) => ({ label: project.name, value: project.id }))}
                          />
                        </div>
                      </div>
                    )}

                    {upload.warning && (
                      <Alert>
                        <AlertDescription>{upload.warning}</AlertDescription>
                      </Alert>
                    )}
                    {upload.error && (
                      <Alert variant="destructive">
                        <AlertDescription>{upload.error}</AlertDescription>
                      </Alert>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bids Table */}
      <div className="space-y-6">
        {/* Action Buttons */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {!vendorView && selectedBids.length > 1 && (
              <SecondaryButton onClick={() => onCompareBids?.(selectedBids)}>
                Compare Selected ({selectedBids.length})
              </SecondaryButton>
            )}
          </div>
          <div className="flex items-center gap-2">
            <NewItemButton
              label="New"
              onClick={() => {
                setSelectedBid(null);
                setSelectedBidItems([]);
                setBidAttachments([]);
                setShowBidForm(true);
              }}
            />
            <SearchButton
              onSearch={list.setSearchInput}
              placeholder="Search"
            />
            {!vendorView && <ThreeDotMenuButton items={menuItems} />}
          </div>
        </div>

        {/* Error Alert */}
        {list.error && (
          <Alert variant="destructive">
            <AlertDescription>{list.error}</AlertDescription>
          </Alert>
        )}

        {/* Table or Empty State */}
        {!list.hasLoaded ? (
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="flex items-center justify-center gap-1.5 mb-4">
                <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></div>
                <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
              </div>
              <p className="text-sm text-gray-500 font-medium">Loading...</p>
            </div>
          </div>
        ) : (
          <>
            {/* the drop box (PDF + AI) stays above the table for the company, with or without bids */}
            {!vendorView && (
              <div
                {...getRootProps()}
                className={`
                  border-2 border-dashed rounded-lg p-8 text-center transition-colors
                  ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
                  ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}
                `}
              >
                <input {...getInputProps()} />
                <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                {isDragActive ? (
                  <p className="text-lg font-medium text-blue-600">Drop the PDF files here...</p>
                ) : (
                  <p className="text-lg font-medium text-gray-700">Drag & drop PDF bid documents here</p>
                )}
              </div>
            )}
            <div className="relative max-w-full overflow-hidden">
              <GenericTable
                {...list.tableProps}
                columns={columns}
                selectable={!vendorView}
                selectedKeys={selectedBids}
                onSelectionChange={setSelectedBids}
                onRowClick={handleRowClick}
                rowKey={(row) => row.id}
                scrollContainerMaxWidth="calc(100vw - 19.2rem)"
                minTableWidth={1200}
              />
            </div>
          </>
        )}
      </div>
    </div>
    </>
  );
}
